import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, HandHeart, MapPinned } from "lucide-react";

const OFFICE_IMG =
  "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODMwOTE0MTF8MA&ixlib=rb-4.1.0&q=85";
const MEAT_IMG =
  "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMHByZW1pdW0lMjBjdXQlMjBtZWF0fGVufDB8fHx8MTc4MzA5MTQxMnww&ixlib=rb-4.1.0&q=85";
const SHEEP_IMG =
  "https://images.unsplash.com/photo-1517347723389-1983cac7e239?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxzaGVlcCUyMGZsb2NrJTIwZ3JhemluZ3xlbnwwfHx8fDE3ODMwOTE0MTJ8MA&ixlib=rb-4.1.0&q=85";

const PRINCIPLES = [
  {
    icon: Sparkles,
    title: "Craft",
    body: "We ship consumer experiences we would use ourselves — from the meat counter to the investor dashboard.",
    span: "lg:col-span-1",
  },
  {
    icon: HandHeart,
    title: "Ownership",
    body: "We build only what we run. Every cut, every flock, every rupee moves through our own operations.",
    span: "lg:col-span-1",
  },
  {
    icon: MapPinned,
    title: "On-ground",
    body: "Hyderabad-based studio with people in farms and slaughterhouses — tech grounded in the real economy.",
    span: "lg:col-span-1",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-bone-300"
      >
        <div className="absolute inset-0 grid-lines opacity-[0.35] pointer-events-none" />
        <div className="container-x pt-16 sm:pt-24 pb-16 sm:pb-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="overline" data-testid="hero-overline">
                / Thiruhanarayano Innovations LLP · Est. 2024
              </div>
              <h1
                className="mt-6 font-display font-black text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.95] tracking-tighter text-navy"
                data-testid="hero-heading"
              >
                LiveCuts.
                <br />
                <span className="font-serif italic font-light text-gold-deep">
                  Flockfolio.
                </span>
                <br />
                Two brands. One studio.
              </h1>
              <p
                className="mt-8 text-lg sm:text-xl text-ink-soft max-w-2xl leading-relaxed"
                data-testid="hero-subheading"
              >
                Thiruhanarayano Innovations LLP runs two consumer-facing
                ventures out of Hyderabad —{" "}
                <strong className="text-navy">LiveCuts</strong>, a fresh-meat
                delivery app, and{" "}
                <strong className="text-navy">Flockfolio</strong>, a
                transparent sheep-flock investment platform.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/products/livecuts"
                  className="btn-primary"
                  data-testid="hero-cta-primary"
                >
                  Explore LiveCuts
                  <ArrowUpRight size={18} />
                </Link>
                <Link
                  to="/products/flockfolio"
                  className="btn-ghost"
                  data-testid="hero-cta-secondary"
                >
                  Explore Flockfolio
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="relative aspect-[4/5] overflow-hidden border border-bone-300">
                <img
                  src={OFFICE_IMG}
                  alt="Modern studio"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-olive/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-bone">
                  <div className="mono-label text-bone/80">/ Studio · Hyderabad</div>
                  <div className="font-display font-semibold mt-1">
                    Two products. Countless problems solved.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-bone-300 border border-bone-300">
            {[
              { k: "2", v: "In-house brands" },
              { k: "1", v: "Studio, Hyderabad" },
              { k: "100%", v: "On-ground audit" },
              { k: "IST", v: "Time zone" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-bone p-6"
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-display font-black text-4xl text-ink-olive tracking-tighter">
                  {s.k}
                </div>
                <div className="mono-label text-ink-soft mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES BENTO */}
      <section
        data-testid="services-section"
        className="border-b border-bone-300"
      >
        <div className="container-x py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
            <div className="lg:col-span-6">
              <div className="overline">/ 01 · How we work</div>
              <h2 className="mt-4 font-serif italic font-light text-4xl sm:text-6xl tracking-tight text-ink-olive">
                Two brands.
                <br />
                One quiet studio.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 text-ink-soft text-lg">
              We are not consultants. Everything on this site is a product we
              own, operate and answer for — from the meat that reaches your
              kitchen to the flock behind your investment.
            </div>
          </div>

          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            data-testid="services-bento-grid"
          >
            {PRINCIPLES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className={`group border border-bone-300 bg-white p-8 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 ${s.span}`}
                  data-testid={`service-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center justify-center w-11 h-11 bg-bone-100 border border-bone-300 text-ink-olive">
                      <Icon size={20} />
                    </span>
                    <span className="mono-label text-terracotta">/ 0{i + 1}</span>
                  </div>
                  <h3 className="mt-8 font-display font-bold text-2xl tracking-tight text-ink-olive">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-ink-soft leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FRESHCUT PRODUCT */}
      <section
        data-testid="meat-app-showcase"
        className="border-b border-bone-300 bg-[#F2ECE2]"
      >
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline text-terracotta">/ 02 · In-house product</div>
            <h2 className="mt-4 font-display font-black text-5xl sm:text-6xl tracking-tighter text-terracotta">
              LiveCuts.
            </h2>
            <div className="mt-2 font-serif italic text-2xl text-ink-olive">
              Fresh meat, delivered from farm to kitchen.
            </div>
            <p className="mt-6 text-ink-soft text-lg leading-relaxed max-w-xl">
              A homegrown fresh-meat delivery app engineered in-house. Cold-chain
              logistics, transparent sourcing from partner farms, chef-grade
              cuts and a mobile experience that turns weekly meat runs into a
              two-tap ritual.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-px bg-terracotta/20 border border-terracotta/20">
              {[
                { k: "45 min", v: "Avg delivery" },
                { k: "12", v: "Farm partners" },
                { k: "4.8★", v: "Avg rating" },
              ].map((s, i) => (
                <div key={i} className="bg-[#F2ECE2] p-5" data-testid={`freshcut-stat-${i}`}>
                  <div className="font-display font-black text-2xl text-terracotta">{s.k}</div>
                  <div className="mono-label text-ink-soft mt-1">{s.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to="/products/livecuts"
                className="inline-flex items-center gap-2 bg-terracotta text-bone px-6 py-3 font-sans font-medium hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(140,58,58,0.35)] transition-all duration-300"
                data-testid="freshcut-cta"
              >
                Explore LiveCuts <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/5] overflow-hidden border border-terracotta/30">
              <img
                src={MEAT_IMG}
                alt="LiveCuts premium meat cuts"
                className="w-full h-full object-cover"
                data-testid="freshcut-image"
              />
              <div className="absolute top-4 left-4 mono-label bg-terracotta text-bone px-2 py-1">
                / LiveCuts · v2.0
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FLOCKFOLIO PRODUCT */}
      <section
        data-testid="sheep-invest-tracker"
        className="border-b border-bone-300 bg-moss text-bone relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.08] grid-lines pointer-events-none" />
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6">
            <div className="relative aspect-[5/4] overflow-hidden border border-bone/20">
              <img
                src={SHEEP_IMG}
                alt="Sheep flock grazing"
                className="w-full h-full object-cover"
                data-testid="flockfolio-image"
              />
              <div className="absolute top-4 left-4 mono-label bg-bone text-moss px-2 py-1">
                / Flockfolio · Live
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="overline text-bone/70">/ 03 · In-house product</div>
            <h2 className="mt-4 font-display font-black text-5xl sm:text-6xl tracking-tighter">
              Flockfolio.
            </h2>
            <div className="mt-2 font-serif italic text-2xl text-bone/80">
              Invest in sheep. Track growth. Share the yield.
            </div>
            <p className="mt-6 text-bone/80 text-lg leading-relaxed max-w-xl">
              Fractional livestock investing, done transparently. Investors
              back real sheep flocks managed by our operations team, then track
              health, growth and returns from a clean dashboard.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-px bg-bone/20 border border-bone/20">
              {[
                { k: "18-24%", v: "Target IRR" },
                { k: "6 mo", v: "Avg cycle" },
                { k: "100%", v: "On-ground audit" },
              ].map((s, i) => (
                <div key={i} className="bg-moss p-5" data-testid={`flockfolio-stat-${i}`}>
                  <div className="font-display font-black text-2xl text-bone">{s.k}</div>
                  <div className="mono-label text-bone/70 mt-1">{s.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to="/products/flockfolio"
                className="inline-flex items-center gap-2 bg-bone text-moss px-6 py-3 font-sans font-medium hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(247,245,240,0.25)] transition-all duration-300"
                data-testid="flockfolio-cta"
              >
                Explore Flockfolio <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-ink-olive text-bone" data-testid="home-cta-strip">
        <div className="container-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <h3 className="lg:col-span-8 font-display font-black text-4xl sm:text-6xl tracking-tighter">
            Curious about our world?
            <br />
            <span className="font-serif italic font-light">Come say hello.</span>
          </h3>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-bone text-ink-olive px-8 py-4 font-sans font-medium hover:bg-terracotta hover:text-bone transition-colors"
              data-testid="home-cta-strip-button"
            >
              Start a conversation <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
